import React from "react";
import { Database, BrainCircuit, TrendingUp, Users, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DemoRequestModal } from "@/components/DemoRequestModal";
import coast from "@/assets/landing-coast.jpg";

// Landing pillars (mockup: DATA / AI / INSIGHTS / COLLABORATION).
const PILLARS = [
  { icon: Database, label: "Data", sub: "Données fiables" },
  { icon: BrainCircuit, label: "AI", sub: "Analyses prédictives" },
  { icon: TrendingUp, label: "Insights", sub: "Opportunités concrètes" },
  { icon: Users, label: "Collaboration", sub: "Partenaires mobilisés" },
];

const DecorativeCurves = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 260 160"
    className="pointer-events-none absolute bottom-0 right-0 hidden h-40 w-64 text-[#C6A662] lg:block"
    style={{ opacity: 0.14 }}
  >
    <g fill="none" stroke="currentColor" strokeWidth="1">
      <path d="M260 150 C 190 150 120 120 60 60" />
      <path d="M260 130 C 200 130 130 100 78 46" />
      <path d="M260 110 C 205 110 145 84 98 36" />
      <path d="M260 92 C 210 92 160 70 120 30" />
    </g>
  </svg>
);

export const AuthShell = ({ title, subtitle, children, footer, rich = false }) => {
  // -------- Login landing (mockup reproduction) --------
  if (rich) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FAFAF6] lg:flex-row">
        {/* LEFT — marketing panel (~60%) */}
        <div className="relative flex w-full flex-col justify-between overflow-hidden bg-[#12281C] px-6 py-10 sm:px-10 lg:w-[60%] lg:px-14 lg:py-12">
          {/* coastal landscape (local asset), anchored to the lower part */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-cover bg-bottom"
            style={{ backgroundImage: `url(${coast})` }}
          />
          {/* dark forest-green gradient — keeps the top dark for white-text contrast */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-b from-[#0C1B13] via-[#11241A]/92 to-[#11241A]/45"
          />
          {/* warm golden glow concentrated in the lower-left */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(60% 45% at 8% 92%, rgba(214,175,102,0.30), transparent 70%)",
            }}
          />

          {/* Brand */}
          <div className="relative z-10 flex items-center gap-3">
            <Logo onDark className="h-14" />
            <div className="leading-tight">
              <div className="whitespace-nowrap font-heading text-[1.75rem] font-bold tracking-tight text-white">
                Lodge <span className="text-[#94A3B8]">&amp; Co</span>
              </div>
              <div className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#9DB0A2]">
                Data · AI · Tourism
              </div>
            </div>
          </div>

          {/* Marketing content */}
          <div className="relative z-10 mt-10 lg:mt-0">
            <h2 className="font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-white lg:text-[3.15rem]">
              L’intelligence touristique
              <br />
              qui transforme vos données
              <br />
              <span className="text-[#79C89B]">en croissance.</span>
            </h2>

            <p className="mt-5 text-lg font-medium text-white/90">
              Data, IA et collaboration au service de votre destination.
            </p>
            <div className="mt-4 h-[3px] w-16 rounded-full bg-[#C6A662]" />

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/70">
              Détectez les opportunités, activez les bons marchés et fédérez vos partenaires
              pour générer plus de visibilité, de réservations et de retombées économiques.
            </p>

            {/* Four benefits */}
            <div className="mt-9 grid max-w-2xl grid-cols-2 gap-x-4 gap-y-7 lg:grid-cols-4">
              {PILLARS.map(({ icon: Icon, label, sub }, i) => (
                <div
                  key={label}
                  className={`flex flex-col gap-2.5 lg:px-5 lg:first:pl-0 ${
                    i > 0 ? "lg:border-l lg:border-white/15" : ""
                  }`}
                >
                  <Icon className="h-7 w-7 text-[#C6A662]" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-white">{label}</div>
                    <div className="mt-1 text-[12px] leading-snug text-white/70">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Call to action — opens the prospect demo-request modal */}
            <DemoRequestModal />
          </div>

          {/* Trust cartouche */}
          <div className="relative z-10 mt-9 flex max-w-md items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-4 py-3.5 backdrop-blur-md">
            <ShieldCheck className="h-5 w-5 shrink-0 text-[#C6A662]" strokeWidth={1.6} />
            <p className="text-xs leading-relaxed text-white/80">
              <span className="font-semibold text-white">Une plateforme sécurisée et évolutive</span>
              <br />
              au service d’un tourisme plus performant et durable.
            </p>
          </div>
        </div>

        {/* RIGHT — form panel (~40%) */}
        <div className="relative flex w-full items-center justify-center px-6 py-14 lg:flex-1 lg:py-12">
          {/* Top-right tagline */}
          <div className="absolute right-6 top-8 hidden text-right lg:right-14 lg:block">
            <div className="text-[11px] font-semibold uppercase leading-relaxed tracking-[0.25em] text-slate-400">
              Des destinations
              <br />
              plus fortes demain
            </div>
            <div className="ml-auto mt-2 h-[3px] w-10 rounded-full bg-[#C6A662]" />
          </div>

          <div className="w-full max-w-md">
            <h1 className="font-heading text-4xl font-bold tracking-tight text-[#1E3D2B]">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
          </div>

          {/* Bottom-right signature */}
          <div className="pointer-events-none absolute bottom-8 right-6 hidden text-right lg:right-14 lg:block">
            <div className="text-[11px] font-medium uppercase tracking-[0.25em] text-slate-400">
              Data · People · Better Destinations
            </div>
            <div className="ml-auto mt-1.5 h-[2px] w-8 bg-[#C6A662]" />
          </div>

          <DecorativeCurves />
        </div>
      </div>
    );
  }

  // -------- Secondary auth pages (Register / Forgot / Reset) --------
  return (
    <div className="flex min-h-screen bg-[#FAFAF6]">
      <div className="relative hidden w-[42%] flex-col justify-between overflow-hidden bg-[#1E3D2B] p-12 lg:flex">
        <div className="relative z-10 flex items-center gap-3">
          <Logo onDark className="h-12" />
          <div className="leading-tight">
            <div className="font-heading text-lg font-bold tracking-tight text-white">
              Lodge <span className="text-[#94A3B8]">&amp; Co</span>
            </div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-[#64748B]">
              Data · AI · Tourism
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight text-white">
            La plateforme Data &amp; AI<br />pour les acteurs du tourisme.
          </h2>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#94A3B8]">
            Détectez les opportunités, activez vos marchés et collaborez avec vos partenaires —
            au sein de votre propre organisation, en toute confidentialité.
          </p>
        </div>

        <p className="relative z-10 text-xs text-[#475569]">© {new Date().getFullYear()} Lodge & Co · Enterprise</p>
        <div className="pointer-events-none absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-[#3E7256] opacity-20 blur-3xl" />
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <Logo className="h-11" />
              <span className="font-heading text-lg font-bold text-[#1E3D2B]">Lodge &amp; Co</span>
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B]">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

export const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <input
      {...props}
      className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3.5 text-sm text-[#17201A] outline-none transition-colors placeholder:text-slate-400 focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
    />
  </div>
);

export const SubmitBtn = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading || props.disabled}
    className="flex h-11 w-full items-center justify-center rounded-lg bg-[#1E3D2B] text-sm font-semibold text-white transition-colors hover:bg-[#2C5540] disabled:cursor-not-allowed disabled:opacity-60"
  >
    {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : children}
  </button>
);

export const ErrorNote = ({ children }) =>
  children ? (
    <div
      data-testid="auth-error"
      className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700"
    >
      {children}
    </div>
  ) : null;
